import type { FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { Card } from "@jti/core-components/components/Card";
import { Loader } from "@jti/core-components/components/Loader";
import type { OrderItem } from "@jti/magento";
import { useTranslations } from "@jti/ui";

import { useCartItemsGrouping } from "@checkout/hooks/useCartItemsGrouping";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import getCartItemsCount from "@checkout/utils/getCartItemsCount";

import SummaryCartBundleItem from "./SummaryCartBundleItem";
import SummaryCartItem from "./SummaryCartItem";

import styles from "./SummaryCartItems.module.scss";

type SummaryCartItemsProps = {
  items: OrderItem[];
  isLoading?: boolean;
};

export const SummaryCartItems: FC<SummaryCartItemsProps> = ({ items, isLoading = false }) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const { isFreeTrial } = useFreeTrial();
  const { payableItems, groupedGiftItems } = useCartItemsGrouping(items);

  const itemsCount = getCartItemsCount(items);

  let cardTitle = __("cart.item_summary.title", [itemsCount]);
  if (itemsCount === 1) {
    cardTitle = __("cart.item_summary.single.title", [], cardTitle);
  }

  const collapsibleProps = itemsCount > 0 ? ({ isCollapsible: true, defaultCollapsed: true } as const) : ({} as const);

  return isLoading ? (
    <Loader />
  ) : (
    <div className={styles.container}>
      <Card variant="level-2" title={cardTitle} {...collapsibleProps}>
        <ul className={styles.list}>
          {[...payableItems, ...groupedGiftItems].map((item) =>
            (item.type_id === "bundle" || item.product_type === "bundle") && config.displayBundleSelectedItems ? (
              <SummaryCartBundleItem {...item} key={item.sku} isFreeTrial={isFreeTrial} />
            ) : (
              <SummaryCartItem {...item} key={item.sku} isFreeTrial={isFreeTrial} />
            )
          )}
        </ul>
      </Card>
    </div>
  );
};
